import { cn } from '@/lib/utils'
import { env } from '@/lib/env'
import { useEffect, useState } from 'react'
import { useSettingsContext } from '@/contexts/SettingsContext'

type CorporateLoadingPlaceholderProps = {
  message?: string
  helperText?: string
  className?: string
  brandLabel?: string
  logoAlt?: string
}

const BRANDING_CONFIG: Record<string, { logoSrc: string; brandLabel: string; logoAlt: string }> = {
  siemens: {
    logoSrc: '/assets/branding/siemens/android-chrome-512x512.png',
    brandLabel: 'Knowledge|Hub',
    logoAlt: 'Siemens Knowledge Hub',
  },
}

const DEFAULT_BRANDING = {
  logoSrc: '/assets/android-chrome-512x512.png',
  brandLabel: 'Corporate|Digital Brain',
  logoAlt: 'Corporate Digital Brain',
}

export function CorporateLoadingPlaceholder({
  message,
  helperText,
  className,
  brandLabel,
  logoAlt,
}: CorporateLoadingPlaceholderProps) {
  const { currentTheme, settings } = useSettingsContext()
  const branding = BRANDING_CONFIG[env.appBranding] ?? DEFAULT_BRANDING
  const resolvedBrandLabel = brandLabel ?? branding.brandLabel
  const resolvedLogoAlt = logoAlt ?? branding.logoAlt
  const [primaryBrandLine, secondaryBrandLine] = resolvedBrandLabel.split('|')
  const [resolvedLogoSrc, setResolvedLogoSrc] = useState(branding.logoSrc)
  const isDefaultBranding = branding === DEFAULT_BRANDING
  const isDarkTheme = currentTheme?.is_dark_mode ?? settings.darkMode

  useEffect(() => {
    let objectUrl: string | null = null
    let isCancelled = false

    if (!isDefaultBranding || !isDarkTheme) {
      setResolvedLogoSrc(branding.logoSrc)
      return
    }

    const image = new Image()
    image.decoding = 'async'
    image.src = branding.logoSrc

    image.onload = () => {
      if (isCancelled) return

      const canvas = document.createElement('canvas')
      canvas.width = image.naturalWidth
      canvas.height = image.naturalHeight

      const context = canvas.getContext('2d')
      if (!context) {
        setResolvedLogoSrc(branding.logoSrc)
        return
      }

      context.drawImage(image, 0, 0)
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height)
      const { data } = imageData

      for (let index = 0; index < data.length; index += 4) {
        const alpha = data[index + 3]
        if (alpha < 16) continue

        const red = data[index]
        const green = data[index + 1]
        const blue = data[index + 2]
        const max = Math.max(red, green, blue)

        // Replace the dark blue logo segments with white while preserving the yellow accents.
        if (max < 110) {
          data[index] = 255
          data[index + 1] = 255
          data[index + 2] = 255
        }
      }

      context.putImageData(imageData, 0, 0)
      canvas.toBlob((blob) => {
        if (!blob || isCancelled) {
          setResolvedLogoSrc(branding.logoSrc)
          return
        }

        objectUrl = URL.createObjectURL(blob)
        setResolvedLogoSrc(objectUrl)
      }, 'image/png')
    }

    image.onerror = () => {
      if (!isCancelled) {
        setResolvedLogoSrc(branding.logoSrc)
      }
    }

    return () => {
      isCancelled = true
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [branding.logoSrc, isDarkTheme, isDefaultBranding])

  return (
    <div className={cn('cdb-loading-placeholder', className)}>
      <div className="cdb-loading-placeholder__frame" aria-hidden="true">
        <span className="cdb-loading-placeholder__halo" />
        <span className="cdb-loading-placeholder__ring" />
        <img
          src={resolvedLogoSrc}
          alt={resolvedLogoAlt}
          className="cdb-loading-placeholder__logo"
          draggable={false}
        />
      </div>

      <p className="cdb-loading-placeholder__brand">
        <span className="cdb-loading-placeholder__brand-line cdb-loading-placeholder__brand-line--primary">
          {primaryBrandLine ?? resolvedBrandLabel}
        </span>
        {secondaryBrandLine ? (
          <span className="cdb-loading-placeholder__brand-line cdb-loading-placeholder__brand-line--accent">
            {secondaryBrandLine}
          </span>
        ) : null}
      </p>

      {message ? (
        <p className="cdb-loading-placeholder__message">{message}</p>
      ) : null}

      {helperText ? (
        <p className="cdb-loading-placeholder__helper">{helperText}</p>
      ) : null}
    </div>
  )
}

export const DriveLoadingPlaceholder = CorporateLoadingPlaceholder
